// Interactive geography choropleth for the Audience tab (#5971). Replaces the flat
// react-simple-maps SVG with the app's Leaflet + MapLibre stack (the same one the
// business location picker uses): a real tiled basemap, pan/zoom, per-country
// shading by a selectable metric, hover tooltips, and a legend.
import { useEffect, useMemo, useState } from 'react';
import { MapContainer, GeoJSON } from 'react-leaflet';
import type { Layer, PathOptions } from 'leaflet';
import type { Feature, FeatureCollection, Geometry } from 'geojson';
import 'leaflet/dist/leaflet.css';

import type { GeographyRow } from '@/lib/apis';
import { deriveMetrics, formatRoas } from '@/lib/performance/metrics';
import { formatMoney } from '@/lib/performance/money';
import { MapLibreTiles } from '@/lib/maps/MapLibreTiles';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

// Match Google Ads country names to the bundled world-atlas `properties.name`.
// Lowercase + strip, plus the handful of aliases that actually differ.
const COUNTRY_ALIASES: Record<string, string> = {
  'united states of america': 'united states',
  usa: 'united states',
  'u.s.a.': 'united states',
  uk: 'united kingdom',
  'republic of ireland': 'ireland',
  'czech republic': 'czechia',
  'south korea': 'korea, republic of',
  'russian federation': 'russia',
};
function normaliseCountry(name: string): string {
  const key = name.trim().toLowerCase();
  return COUNTRY_ALIASES[key] ?? key;
}

interface MetricDef {
  id: string;
  label: string;
  /** Metric value for a country row; null when not applicable (e.g. ROAS with no value). */
  valueOf: (r: GeographyRow) => number | null;
  format: (v: number, cs: string) => string;
}

const METRICS: MetricDef[] = [
  { id: 'conversions', label: 'Conversions', valueOf: (r) => r.conversions, format: (v) => Math.round(v).toLocaleString() },
  { id: 'spend', label: 'Cost', valueOf: (r) => r.cost, format: (v, cs) => formatMoney(v, cs) },
  {
    id: 'roas',
    label: 'ROAS',
    valueOf: (r) => deriveMetrics({ impressions: r.impressions, clicks: r.clicks, results: r.conversions, spend: r.cost, conversionValue: r.conversion_value }).roas,
    format: (v) => formatRoas(v),
  },
  { id: 'cpc', label: 'CPC', valueOf: (r) => (r.avg_cpc > 0 ? r.avg_cpc : null), format: (v, cs) => formatMoney(v, cs) },
  { id: 'cost_per_conv', label: 'Cost / conv.', valueOf: (r) => (r.conversions > 0 ? r.cost_per_conv : null), format: (v, cs) => formatMoney(v, cs) },
];

const NO_DATA_STYLE: PathOptions = { color: '#e5e7eb', weight: 0.5, fillColor: '#eef2f7', fillOpacity: 0.6 };
const HOVER_STYLE: PathOptions = { color: '#2309C5', weight: 1.2, fillColor: '#290BD9', fillOpacity: 0.85 };

/** Interactive choropleth of account geography, shaded by the selected metric. */
export function GeographyChoropleth({ rows, cs }: { rows: GeographyRow[]; cs: string }) {
  const [metricId, setMetricId] = useState('conversions');
  const [geo, setGeo] = useState<FeatureCollection | null>(null);

  useEffect(() => {
    let alive = true;
    // Country polygons bundled with the app (open data) — same source as the
    // business location picker; no CDN round-trip.
    fetch('/geo/countries.geo.json')
      .then((r) => r.json())
      .then((d) => { if (alive) setGeo(d as FeatureCollection); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const metric = METRICS.find((m) => m.id === metricId) ?? METRICS[0];

  const byCountry = useMemo(() => {
    const m = new Map<string, GeographyRow>();
    for (const r of rows) m.set(normaliseCountry(r.country), r);
    return m;
  }, [rows]);

  const max = useMemo(() => {
    let mx = 0;
    for (const r of rows) {
      const v = metric.valueOf(r);
      if (v != null && Number.isFinite(v) && v > mx) mx = v;
    }
    return mx;
  }, [rows, metric]);

  const styleFor = useMemo(() => {
    return (name: string): PathOptions => {
      const row = byCountry.get(normaliseCountry(name));
      const v = row ? metric.valueOf(row) : null;
      if (row == null || v == null || !Number.isFinite(v) || max <= 0) return NO_DATA_STYLE;
      const intensity = Math.min(1, v / max);
      return { color: '#ffffff', weight: 0.5, fillColor: '#290BD9', fillOpacity: 0.15 + 0.7 * intensity };
    };
  }, [byCountry, metric, max]);

  const onEachCountry = (feature: Feature<Geometry, { name?: string }>, layer: Layer) => {
    const name = feature.properties?.name ?? '';
    const row = byCountry.get(normaliseCountry(name));
    const setStyle = (layer as Layer & { setStyle?: (s: PathOptions) => void }).setStyle?.bind(layer);
    if (row) {
      const v = metric.valueOf(row);
      const val = v != null && Number.isFinite(v) ? metric.format(v, cs) : '—';
      layer.bindTooltip(
        `<span style="font-weight:600">${name}</span><br/>${metric.label}: ${val}` +
          `<br/><span style="color:#64748b">${Math.round(row.conversions).toLocaleString()} conv · ${formatMoney(row.cost, cs)}</span>`,
        { sticky: true },
      );
    }
    layer.on('mouseover', () => setStyle?.(HOVER_STYLE));
    layer.on('mouseout', () => setStyle?.(styleFor(name)));
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-end">
        <Select value={metricId} onValueChange={setMetricId}>
          <SelectTrigger className="h-8 w-[150px] text-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {METRICS.map((m) => (
              <SelectItem key={m.id} value={m.id}>{m.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="relative overflow-hidden rounded-lg border border-gray-200" style={{ height: 360 }}>
        {geo ? (
          <MapContainer
            center={[25, 5]}
            zoom={1.5}
            minZoom={1}
            worldCopyJump
            scrollWheelZoom={false}
            style={{ height: '100%', width: '100%', background: '#f8fafc' }}
          >
            <MapLibreTiles />
            {/* Remount on metric change so styles + tooltips re-apply. */}
            <GeoJSON key={metricId} data={geo} style={(f) => styleFor((f?.properties as { name?: string })?.name ?? '')} onEachFeature={onEachCountry} />
          </MapContainer>
        ) : (
          <div className="flex h-full items-center justify-center text-sm text-gray-400">Loading map…</div>
        )}
        <div className="pointer-events-none absolute bottom-3 left-3 z-[1000] rounded-md bg-white/90 px-2.5 py-1.5 text-[11px] shadow-sm ring-1 ring-gray-200">
          <div className="mb-1 font-medium text-gray-700">{metric.label}</div>
          <div className="flex items-center gap-1.5">
            <span className="text-gray-400">Low</span>
            <span className="h-2 w-24 rounded-sm" style={{ background: 'linear-gradient(to right, rgba(41,11,217,0.15), rgba(41,11,217,0.85))' }} />
            <span className="text-gray-400">High</span>
          </div>
        </div>
      </div>
    </div>
  );
}
